// Schedule report - a print-ready page (browser Print -> Save as PDF) of the
// whole schedule: summary, full-plan timeline, and the complete activity
// tree with each activity's dates / status / progress / responsible party,
// its predecessors and successors, and the linked issues (issue / action /
// RFI), checklist items and meeting minutes.
//
//   /schedule-print/:projectId                 whole project
//   /schedule-print/:projectId?company=<id>    one contractor's portion only:
//                                              their activities (+ parents for
//                                              context), their issues, their
//                                              people's checklist items
//   ?ownerSignOff=signed,planned,not            the schedule chart's owner/GC
//                                              filter (parents kept as context)
// Opened from the dashboard / Schedule page ("Print / PDF"); ?autoprint=1
// triggers the print dialog once the data is in.
import { PartnerWithTrades } from "components/Partners/PartnerTradeLabel";
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Button, Select, Spin } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import { url, tokenRef } from "api";
import { flattenTree } from "components/CoordinationGantt/ganttHelpers";
import { isMilestone, isMeeting } from "helpers/schedule/activityTypes";
import { depId } from "helpers/schedule/predecessors";
import { PRINT_GROUPS, groupRows } from "helpers/schedule/printGroups";
import { issueLabel, prefixOf } from "helpers/artifactPrefix";
import { forcedSignOff } from "components/CoordinationGantt/signOffForced";
import { movedSignOff } from "components/CoordinationGantt/signOffMoved";
import { ownerStarDay, ownerStarOn, ownerStarColor, ownerSignOffText, ownerFilterScope, ownerFilterWords, printQuery, OWNER_STAR_BLUE, OWNER_STAR_PLANNED } from "helpers/schedule/ownerSignOffPrint";
import OwnerSignOffFilter from "components/Coordination/OwnerSignOffFilter";
import { issueActivityIds } from "helpers/schedule/issueActivityIds";

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(tokenRef)}` });
const api = async (path) => { const r = await fetch(`${url}${path}`, { headers: auth() }); if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); };
const d = (v) => (v ? new Date(v).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");
const STATUS = { planned: "Planned", in_progress: "In progress", done: "Done", on_hold: "On hold" };
const TYPE = { task: "Task", milestone: "Milestone", meeting: "Meeting", signoff: "Sign-off", area_release: "Area release" };
const KIND = (i) => (i.category === "rfi" || (i.rfi && i.rfi.number) ? "RFI" : i.category === "action" ? "Action" : "Issue");
const days = (a, b) => (a && b ? Math.max(1, Math.round((new Date(b) - new Date(a)) / 86400000) + 1) : "");

export default function SchedulePrintPage() {
  const { projectId } = useParams();
  const location = useLocation();
  const qs = new URLSearchParams(location.search);
  const [company, setCompany] = useState(qs.get("company") || "");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [title, setTitle] = useState("");
  const [groupBy, setGroupBy] = useState(qs.get("groupBy") || "wbs");
  const [ownerFilter, setOwnerFilter] = useState(qs.get("ownerSignOff") || "");

  // Labels for the two axes that group by KEY rather than by a word: an
  // area id and a project-stage key. Both fail quiet - a lane falls back
  // to the raw key, which still groups correctly.
  const [areaNames, setAreaNames] = useState({});
  const [stageLabels, setStageLabels] = useState({});
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { listAreaRows } = require("api/areas/areasApi");
        const rows = await listAreaRows(projectId, "approved");
        if (!alive) return;
        const names = {};
        for (const a of rows) names[String(a._id)] = `${a.code ? `${a.code} · ` : ""}${a.name}`;
        setAreaNames(names);
      } catch { /* ignore */ }
      try {
        const { getTaxonomy } = require("api/taxonomy/taxonomyApi");
        const tax = await getTaxonomy();
        if (!alive) return;
        const labels = {};
        for (const r of tax.project_stage || []) labels[r.key] = r.label;
        setStageLabels(labels);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [projectId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [feed, checklist, roster, picker] = await Promise.all([
          api(`/projects/${projectId}/gantt`),
          api(`/projects/${projectId}/checklist`).catch(() => []),
          api(`/projects/${projectId}/participants/roster`).catch(() => []),
          api(`/projects/picker`).catch(() => ({ projects: [] })),
        ]);
        if (!alive) return;
        const p = (picker.projects || picker || []).find((x) => String(x._id) === String(projectId));
        setTitle(p ? p.title : (feed.projectTitle || ""));
        // Names the PDF the browser saves: "SPX-schedule-report-2026-08-20.pdf".
        const pfx = feed.artifactPrefix || prefixOf(p);
        try {
          document.title = `${pfx}-schedule-report-${new Date().toISOString().slice(0, 10)}`;
        } catch (e) { /* title is cosmetic */ }

        // Partners (contractor supervisors / employees) get a feed already
        // scoped to their company and are locked to that scope here.
        if (feed.access && feed.access.partnerCompanyId) setCompany(feed.access.partnerCompanyId);
        setData({ items: feed.items || [], issues: feed.issues || [], checklist: Array.isArray(checklist) ? checklist : [], roster: Array.isArray(roster) ? roster : [], health: feed.health || {}, access: feed.access || {} });
      } catch (e) { if (alive) setErr(e.message || "Could not load the schedule"); }
    })();
    return () => { alive = false; };
  }, [projectId]);

  const report = useMemo(() => {
    if (!data) return null;
    const { items, issues, checklist, roster } = data;
    const nameOf = new Map(roster.map((g) => [String(g.companyId), g.companyName]));
    const byId = new Map(items.map((i) => [String(i._id), i]));
    const ownerCompany = roster.find((g) => g.role === "owner");
    // Contractor scope: their activities + ancestors (context, dimmed),
    // their issues, checklist items of their company / people.
    let scopeIds = null;
    if (company) {
      scopeIds = new Set();
      for (const it of items) if (String(it.responsibleCompanyId || "") === String(company)) scopeIds.add(String(it._id));
      for (const it of items) {
        if (!scopeIds.has(String(it._id))) continue;
        let p = it.parentId ? byId.get(String(it.parentId)) : null;
        while (p && !scopeIds.has(String(p._id))) { scopeIds.add(String(p._id)); p = p.parentId ? byId.get(String(p.parentId)) : null; }
      }
    }
    let visible = scopeIds ? items.filter((i) => scopeIds.has(String(i._id))) : items;
    // Owner/GC filter: a parent kept only for the filter is dimmed context.
    const owner = ownerFilterScope(visible, ownerFilter);
    if (owner) visible = visible.filter((i) => owner.keep.has(String(i._id)));
    const flat = flattenTree(visible).map((r) => ({ ...r, own: (!scopeIds || String(r.item.responsibleCompanyId || "") === String(company)) && !(owner && owner.context.has(String(r.item._id))) }));
    const rows = groupRows(flat, groupBy, {
      areas: areaNames,
      stages: stageLabels,
      companies: Object.fromEntries((data.roster || []).map((g) => [String(g.companyId), g.companyName])),
    });
    const issuesByItem = new Map();
    for (const i of issues) {
      if (company && String(i.responsibleCompanyId || "") !== String(company)) continue;
      // Under every activity it is on (several, all equal).
      const keys = issueActivityIds(i);
      for (const k of keys.length ? keys : [""]) {
        if (!issuesByItem.has(k)) issuesByItem.set(k, []);
        issuesByItem.get(k).push(i);
      }
    }
    const companyEmails = new Set((roster.find((g) => String(g.companyId) === String(company))?.users || []).map((u) => String(u.email || "").toLowerCase()));
    const checksByItem = new Map();
    for (const c of checklist) {
      if (company && !(String(c.responsibleCompanyId || "") === String(company) || companyEmails.has(String(c.currentAssignee?.email || "").toLowerCase()))) continue;
      const k = String((c.ganttItemId && c.ganttItemId._id) || c.ganttItemId || "");
      if (!checksByItem.has(k)) checksByItem.set(k, []);
      checksByItem.get(k).push(c);
    }
    const successors = new Map();
    for (const it of items) for (const dep of it.dependencies || []) { const k = depId(dep); if (!successors.has(k)) successors.set(k, []); successors.get(k).push(it); }
    const allDates = visible.flatMap((i) => [i.startDate, i.endDate, i.signOffDate, movedSignOff(i), ownerStarOn(i) ? ownerStarDay(i) : null]).filter(Boolean).map((x) => +new Date(x));
    const t0 = allDates.length ? Math.min(...allDates) : Date.now(), t1 = allDates.length ? Math.max(...allDates) : Date.now() + 86400000;
    const counted = owner ? visible.filter((i) => !owner.context.has(String(i._id))) : visible;
    // With the owner/GC filter on, the counts are the kept activities' own.
    const countedIds = new Set(counted.map((i) => String(i._id)));
    const scopedIssues = (scopeIds ? issues.filter((i) => String(i.responsibleCompanyId || "") === String(company)) : issues)
      .filter((i) => !owner || issueActivityIds(i).some((k) => countedIds.has(k)));
    const scopedChecks = owner ? [...countedIds].flatMap((k) => checksByItem.get(k) || []) : [...checksByItem.values()].flat();
    const stats = {
      activities: counted.filter((i) => !isMeeting(i.type) && !isMilestone(i.type)).length, milestones: counted.filter((i) => isMilestone(i.type)).length, meetings: counted.filter((i) => isMeeting(i.type)).length,
      done: counted.filter((i) => i.status === "done").length, disabled: counted.filter((i) => i.disabled).length,
      issuesOpen: scopedIssues.filter((i) => ["open", "pending", "under_review"].includes(i.status)).length, issuesTotal: scopedIssues.length,
      actions: scopedIssues.filter((i) => i.category === "action").length, rfis: scopedIssues.filter((i) => i.category === "rfi").length,
      checksDone: scopedChecks.filter((c) => c.status === "complete").length, checksTotal: scopedChecks.length,
    };
    return { rows, byId, issuesByItem, checksByItem, successors, nameOf, t0, t1, stats, ownerCompany, unlinkedIssues: owner ? [] : issuesByItem.get("") || [], unlinkedChecks: owner ? [] : checksByItem.get("") || [], ownerOn: !!owner };
  }, [data, company, ownerFilter]);

  useEffect(() => {
    if (report && qs.get("autoprint") === "1") { const t = setTimeout(() => window.print(), 800); return () => clearTimeout(t); }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report]);

  if (err) return <div style={{ padding: 24 }}>{err}</div>;
  if (!report) return <div style={{ padding: 40, textAlign: "center" }}><Spin /> <div style={{ color: "#888", marginTop: 8 }}>Preparing the schedule report…</div></div>;
  const { rows, byId, issuesByItem, checksByItem, successors, nameOf, t0, t1, stats } = report;
  const span = Math.max(t1 - t0, 86400000);
  const pct = (t) => Math.max(0, Math.min(100, ((+new Date(t) - t0) / span) * 100));
  const today = Date.now();
  const companyName = company ? (nameOf.get(String(company)) || "Contractor") : "";
  const personsOf = (it) => (it.responsibleUsers && it.responsibleUsers.length ? it.responsibleUsers.map((u) => u.name).filter(Boolean).join(", ") : it.responsibleUserName || "");
  // Sign-off stars, the same colours as the Gantt sheet: green signed off,
  // red overdue, gold pending; grey = where a moved sign-off was first planned.
  const starColor = (it) => (it.signedOffAt ? "#2e7d32" : +new Date(it.signOffDate) < today ? "#c62828" : "#d9a400");
  const star = (v, color, label, testid, shift = 0) => (
    <div data-testid={testid} title={label} style={{ position: "absolute", left: `calc(${pct(v)}% - ${6 - shift}px)`, top: -2, fontSize: 12, lineHeight: "16px", color, WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}>★</div>
  );
  const signOffText = (it) => (it.signedOffAt
    ? `★ signed off ${d(it.signedOffAt)}${forcedSignOff(it) ? " (overridden)" : ""}`
    : `☆ due ${d(it.signOffDate)}`) + (movedSignOff(it) ? ` (was ${d(movedSignOff(it))})` : "");
  const barColor = (it) => (it.disabled ? "#bbb" : it.status === "done" ? "#2e7d32" : it.endDate && new Date(it.endDate) < today && it.status !== "done" ? "#c62828" : it.status === "in_progress" ? "#1976d2" : "#9aa0a6");

  return (
    <div className="schedule-print" style={{ fontFamily: "Inter, Arial, sans-serif", color: "#222", maxWidth: 1100, margin: "0 auto", padding: "16px 20px" }}>
      <style>{`
        @page { size: A4 landscape; margin: 12mm; }
        @media print {
          /* Only the report prints - the app shell (nav, sidebar, chat) is hidden. */
          body * { visibility: hidden !important; }
          .schedule-print, .schedule-print * { visibility: visible !important; }
          .schedule-print { position: absolute !important; left: 0; top: 0; width: 100%; padding: 0; max-width: none; margin: 0; }
          .no-print { display: none !important; }
          .act { break-inside: avoid; } h2 { break-after: avoid; }
        }
        .schedule-print { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .schedule-print table { border-collapse: collapse; width: 100%; font-size: 11px; table-layout: fixed; }
        .schedule-print th, .schedule-print td { border: 1px solid #e5e5e5; padding: 3px 6px; text-align: left; vertical-align: top; }
        .schedule-print th { background: #f5f5f7; font-weight: 600; }
        .schedule-print .muted { color: #777; }
        .schedule-print .act { border: 1px solid #e8e8e8; border-radius: 6px; padding: 8px 10px; margin: 8px 0; }
        .schedule-print .act.ctx { opacity: 0.6; }
        .schedule-print .tag { display: inline-block; border: 1px solid #ddd; border-radius: 4px; padding: 0 5px; font-size: 10px; margin-left: 4px; color: #555; }
      `}</style>

      <div className="no-print" style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        <Button type="primary" icon={<PrinterOutlined />} onClick={() => window.print()}>Print / Save as PDF</Button>
        <span className="muted" style={{ fontSize: 12 }}>Scope:</span>
        {data.access?.partnerCompanyId ? (
          <b style={{ fontSize: 12 }}>{companyName || "Your company"} (your portion of the schedule)</b>
        ) : (
          <Select style={{ minWidth: 260 }} value={company || ""} onChange={(v) => setCompany(v || "")}
            options={[{ value: "", label: "Whole project (every contractor)" }, ...data.roster.filter((g) => g.role !== "owner").map((g) => ({ value: String(g.companyId), text: `${g.companyName} only`, label: <PartnerWithTrades name={`${g.companyName} only (their activities, issues, checklists)`} trades={(g.tradeLabels && g.tradeLabels.length ? g.tradeLabels : g.trades) || []} /> }))]}
            optionLabelProp="text" />
        )}
        <span className="muted" style={{ fontSize: 12 }}>Use the browser's print dialog → "Save as PDF". Landscape A4 is preset.</span>
        <span className="muted" style={{ fontSize: 12 }}>Group by:</span>
        <Select style={{ width: 160 }} value={groupBy} onChange={setGroupBy} options={PRINT_GROUPS} />
        <OwnerSignOffFilter width={200} value={ownerFilter} onChange={setOwnerFilter} />
        <a href={`/gantt-print/${projectId}${printQuery(company, ownerFilter)}`} style={{ fontSize: 12 }}>Switch to the full Gantt chart (wide sheet)</a>
      </div>

      <h1 style={{ margin: "0 0 2px", fontSize: 22 }}>{title || "Schedule"} — schedule report{companyName ? ` · ${companyName}` : ""}</h1>
      <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
        Generated {new Date().toLocaleString()} · {stats.activities} activities ({stats.milestones} milestones, {stats.done} done{stats.disabled ? `, ${stats.disabled} disabled` : ""}) · {stats.meetings} meeting series · {stats.issuesTotal} issues ({stats.issuesOpen} open; {stats.actions} actions, {stats.rfis} RFIs) · checklist {stats.checksDone}/{stats.checksTotal} done
        {data.health?.status && !company ? ` · schedule health: ${data.health.status}` : ""}
        {ownerFilter ? ` · owner/GC sign-off: ${ownerFilterWords(ownerFilter)} (issues and checklist items on no activity are left out)` : ""}
      </div>

      {/* Timeline */}
      <h2 style={{ fontSize: 14, margin: "12px 0 6px" }}>Timeline {d(t0)} – {d(t1)}</h2>
      <table style={{ marginBottom: 12 }}>
        <tbody>
          {rows.filter((r) => r.item.startDate && r.item.endDate).slice(0, 400).map(({ item, depth, own }) => {
            const left = pct(item.startDate), right = pct(item.endDate), w = Math.max(right - left, 0.6);
            return (
              <tr key={item._id} className={own ? "" : "ctx"}>
                <td style={{ width: 260, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", paddingLeft: 6 + depth * 10, opacity: own ? 1 : 0.55, textDecoration: item.disabled ? "line-through" : "none" }}>{item.wbs ? `${item.wbs} ` : ""}{item.name}</td>
                <td style={{ padding: 0 }}>
                  <div style={{ position: "relative", height: 14, width: "100%" }}>
                    <div style={{ position: "absolute", left: `${left}%`, width: `${w}%`, top: 3, height: 8, background: barColor(item), borderRadius: 2, opacity: own ? 1 : 0.4, WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }} />
                    {today >= t0 && today <= t1 && <div style={{ position: "absolute", left: `${pct(today)}%`, top: 0, bottom: 0, borderLeft: "1px dashed #c62828" }} />}
                    {movedSignOff(item) && star(movedSignOff(item), "#b0b0b0", `Sign-off originally planned ${d(movedSignOff(item))} - moved to ${d(item.signOffDate)}`, "report-signoff-original")}
                    {item.signOffDate && star(item.signOffDate, starColor(item), signOffText(item), "report-signoff")}
                    {ownerStarOn(item) && star(ownerStarDay(item), ownerStarColor(item), `Owner/GC ${ownerSignOffText(item, d, { long: true })}`, "report-owner-signoff",
                      item.signOffDate && new Date(item.signOffDate).toDateString() === ownerStarDay(item).toDateString() ? 10 : 0)}
                  </div>
                </td>
                <td style={{ width: 200, whiteSpace: "nowrap" }} className="muted">{d(item.startDate)} – {d(item.endDate)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="muted" style={{ fontSize: 10, margin: "-8px 0 10px" }}>Bars: <span style={{ color: "#1976d2" }}>■</span> in progress · <span style={{ color: "#9aa0a6" }}>■</span> planned · <span style={{ color: "#2e7d32" }}>■</span> done · <span style={{ color: "#c62828" }}>■</span> past end, not done · <span style={{ color: "#bbb" }}>■</span> disabled · ★ sign-off (gold = pending, green = signed off, red = overdue, grey = where a moved sign-off was first planned) · <span style={{ color: OWNER_STAR_BLUE }}>★</span> signed off by owner/GC (<span style={{ color: OWNER_STAR_PLANNED }}>★</span> planned) · dashed red line = today{company || ownerFilter ? " · dimmed rows = parents shown for context only" : ""}</div>
      {/* Detail */}
      <h2 style={{ fontSize: 14, margin: "12px 0 6px" }}>Activities, milestones, meetings — with issues, actions, checklists and relationships</h2>
      {rows.map(({ item, depth, own, groupKey, groupLabel }) => {
        // A header row from groupRows() - no activity of its own.
        if (!item) return (
          <h3 key={groupKey} style={{ fontSize: 13, margin: "14px 0 4px", borderBottom: "1px solid #ddd", paddingBottom: 2 }}>{groupLabel}</h3>
        );
        const preds = (item.dependencies || []).map((dep) => byId.get(depId(dep))).filter(Boolean);
        const succ = successors.get(String(item._id)) || [];
        const iss = issuesByItem.get(String(item._id)) || [];
        const chk = checksByItem.get(String(item._id)) || [];
        const parent = item.parentId ? byId.get(String(item.parentId)) : null;
        return (
          <div key={item._id} className={`act${own ? "" : " ctx"}`} style={{ marginLeft: depth * 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
              <div style={{ fontWeight: 600, fontSize: 12.5, textDecoration: item.disabled ? "line-through" : "none" }}>
                {item.wbs ? `${item.wbs} ` : ""}{item.name}
                <span className="tag">{TYPE[item.type] || item.type}</span>
                <span className="tag">{STATUS[item.status] || item.status}{item.status !== "done" && item.progress ? ` · ${Math.round(item.progress)}%` : ""}</span>
                {item.disabled && <span className="tag">disabled</span>}
                {item.healthStatus && item.healthStatus !== "on_track" && item.healthStatus !== "disabled" && <span className="tag" style={{ color: "#c62828", borderColor: "#f5c2c0" }}>{item.healthStatus.replace("_", " ")}</span>}
              </div>
              <div className="muted" style={{ fontSize: 11, whiteSpace: "nowrap" }}>{d(item.startDate)} – {d(item.endDate)}{!isMilestone(item.type) && item.startDate && item.endDate ? ` · ${days(item.startDate, item.endDate)} d` : ""}</div>
            </div>
            <div style={{ fontSize: 11, color: "#444", marginTop: 2 }}>
              {(nameOf.get(String(item.responsibleCompanyId)) || item.contractorLabel) ? <>Responsible: <b>{nameOf.get(String(item.responsibleCompanyId)) || `${item.contractorLabel} (label)`}</b>{personsOf(item) ? ` — ${personsOf(item)}` : ""} · </> : null}
              {item.discipline ? <>Discipline: {item.discipline} · </> : null}
              {(item.areaKey || item.level || item.grid) ? <>Location: {[item.areaKey, item.level, item.grid].filter(Boolean).join(" / ")} · </> : null}
              {item.signOffDate ? <>Sign-off: <b style={{ color: starColor(item) }}>{signOffText(item)}</b> · </> : null}
              {item.ownerSignOff ? <>Signed off by owner/GC: <b style={{ color: ownerStarColor(item) }} data-testid="report-owner-signoff-line">{ownerSignOffText(item, d, { long: true })}</b> · </> : null}
              {parent ? <>Part of: {parent.wbs ? `${parent.wbs} ` : ""}{parent.name} · </> : null}
              {preds.length ? <>After: {preds.map((p) => `${p.wbs ? `${p.wbs} ` : ""}${p.name}`).join("; ")} · </> : null}
              {succ.length ? <>Before: {succ.map((p) => `${p.wbs ? `${p.wbs} ` : ""}${p.name}`).join("; ")} · </> : null}
              {item.updatedAt ? <span className="muted">Last modified {d(item.updatedAt)}{item.lastEditedBy?.name ? ` by ${item.lastEditedBy.name}` : ""}</span> : null}
            </div>
            {item.notes ? <div style={{ fontSize: 11, color: "#555", marginTop: 2, whiteSpace: "pre-wrap" }}>{item.notes}</div> : null}
            {iss.length > 0 && (
              <table style={{ marginTop: 6 }}>
                <thead><tr><th style={{ width: 60 }}>Kind</th><th style={{ width: 70 }}>#</th><th>Title</th><th style={{ width: 90 }}>Status</th><th style={{ width: 70 }}>Priority</th><th style={{ width: 90 }}>Due</th><th style={{ width: 170 }}>Responsible</th></tr></thead>
                <tbody>{iss.map((i) => (
                  <tr key={i._id}><td>{KIND(i)}</td><td>{i.displayId ? `${issueLabel(i)}` : ""}</td><td>{i.title}{i.topic ? <span className="muted"> · {i.topic}</span> : null}</td><td>{i.status}</td><td>{i.priority || ""}</td><td>{d(i.dueDate)}</td><td>{nameOf.get(String(i.responsibleCompanyId)) || i.contractorLabel || ""}</td></tr>
                ))}</tbody>
              </table>
            )}
            {chk.length > 0 && (
              <table style={{ marginTop: 6 }}>
                <thead><tr><th style={{ width: 60 }}>Check</th><th>Item</th><th style={{ width: 110 }}>Status</th><th style={{ width: 90 }}>Due</th><th style={{ width: 170 }}>Responsible</th></tr></thead>
                <tbody>{chk.map((c) => (
                  <tr key={c._id}><td>{c.status === "complete" ? "☑" : "☐"}</td><td>{c.title}{c.group ? <span className="muted"> · {c.group}</span> : null}</td><td>{c.status}</td><td>{d(c.dueDate)}</td><td>{nameOf.get(String(c.responsibleCompanyId)) || c.responsibleUserName || c.currentAssignee?.name || c.contractorLabel || ""}</td></tr>
                ))}</tbody>
              </table>
            )}
          </div>
        );
      })}

      {(report.unlinkedIssues.length > 0 || report.unlinkedChecks.length > 0) && (
        <>
          <h2 style={{ fontSize: 14, margin: "14px 0 6px" }}>Not linked to an activity</h2>
          {report.unlinkedIssues.length > 0 && (
            <table><thead><tr><th style={{ width: 60 }}>Kind</th><th style={{ width: 70 }}>#</th><th>Title</th><th style={{ width: 90 }}>Status</th><th style={{ width: 70 }}>Priority</th><th style={{ width: 90 }}>Due</th><th style={{ width: 170 }}>Responsible</th></tr></thead>
              <tbody>{report.unlinkedIssues.map((i) => <tr key={i._id}><td>{KIND(i)}</td><td>{i.displayId ? `${issueLabel(i)}` : ""}</td><td>{i.title}</td><td>{i.status}</td><td>{i.priority || ""}</td><td>{d(i.dueDate)}</td><td>{nameOf.get(String(i.responsibleCompanyId)) || i.contractorLabel || ""}</td></tr>)}</tbody></table>
          )}
          {report.unlinkedChecks.length > 0 && (
            <table style={{ marginTop: 6 }}><thead><tr><th style={{ width: 60 }}>Check</th><th>Item</th><th style={{ width: 110 }}>Status</th><th style={{ width: 90 }}>Due</th></tr></thead>
              <tbody>{report.unlinkedChecks.map((c) => <tr key={c._id}><td>{c.status === "complete" ? "☑" : "☐"}</td><td>{c.title}</td><td>{c.status}</td><td>{d(c.dueDate)}</td></tr>)}</tbody></table>
          )}
        </>
      )}
    </div>
  );
}
